import { useEffect } from "react";

import { usePlatformAuth } from "./usePlatformAuth";

import useUserStore from "@store/useUserStore";
import useSystemStore from "@store/useSystemStore";
import usePlatformStore from "@store/usePlatformStore";
import useAuthStore, { clearAuthStore } from "@store/useAuthStore";
import { useGetUser } from "@screens/connect-meridian/hooks/useGetUser";
import { useResetApp } from "./useResetApp";
import userService from "@services/user.service";
import { createBearerToken } from "@config/constants";
import {
  ApiError,
  addResponseInterceptor,
  setDefaultHeader,
  deleteDefaultHeader,
  retryRequest,
} from "@config/apiClient";
import { DisconnectedError } from "@app-types/errors";
import { ActiveScreen, ConnectionScreenState } from "@enums/screens.enum";
import { Source } from "@enums/common.enum";

export function useConnection() {
  const { resetApp } = useResetApp();
  const { accessToken, getAccessToken } = usePlatformAuth();
  const { itemId, isOutlook, isChrome } = usePlatformStore();
  const { changeActiveScreen, setConnectionScreenState } = useSystemStore();
  const { hasSession, setHasSession, setUser, resetUser, setIsUserConnected, setIsPending } =
    useUserStore();
  const { authToken, refreshToken, setTokens } = useAuthStore();
  const { data: user, error, isLoading, hasGoogleGrant } = useGetUser(hasSession);

  const sourcePlatform = isOutlook()
    ? Source.OUTLOOK_ADDIN
    : Source.CHROME_EXTENSION;

  useEffect(() => {
    // Outlook SSO can fail (e.g. consent not granted) — catch to prevent
    // unhandled rejection in Bugsnag. accessToken stays null, hasSession
    // remains false, and the user sees the connect screen.
    // Chrome Extension auth never throws, so this catch is a no-op there.
    getAccessToken().catch(() => {});
  }, [authToken, getAccessToken]);

  useEffect(() => {
    if (!!accessToken) {
      setDefaultHeader("Authorization", createBearerToken(accessToken));
      setDefaultHeader("X-Data-Source", sourcePlatform);
      setHasSession(true);
    } else {
      deleteDefaultHeader("Authorization");
      deleteDefaultHeader("X-Data-Source");
      setHasSession(false);
    }
  }, [accessToken, itemId, sourcePlatform]);

  // active user update
  useEffect(() => {
    setIsPending(isLoading);

    if (error) {
      resetUser();
      return;
    }

    if (user) setUser(user);

    if ((isOutlook() && user) || hasGoogleGrant) {
      // isUserConnected should be single source of truth to define
      // if user has connection in Meridian profile
      setIsUserConnected(true);
      // Reset connection screen state so it shows "Connect" instead of "Waiting" on next visit
      setConnectionScreenState(ConnectionScreenState.Connect);
    }
  }, [user, error, isLoading, hasGoogleGrant]);

  // interceptor for 401 and 403 errors
  useEffect(() => {
    let isRefreshing = false;
    let failedQueue: Array<{
      resolve: (value?: any) => void;
      reject: (reason?: any) => void;
      config: ApiError["config"];
    }> = [];

    const processQueue = (error: any, token: string | null = null) => {
      failedQueue.forEach(({ resolve, reject, config }) => {
        if (error) {
          reject(error);
        } else {
          config.headers.Authorization = createBearerToken(token!);
          resolve(retryRequest(config));
        }
      });

      failedQueue = [];
    };

    const eject = addResponseInterceptor(
      undefined,
      async (error) => {
        const err = error as ApiError;

        if (err?.response?.status === 403 && err.config) {
          setIsUserConnected(false);
          return Promise.reject(new DisconnectedError());
        }

        if (err?.response?.status === 401 && err.config) {
          const originalConfig = err.config as any;

          // if we already tried to refresh the token
          if (originalConfig._retry) {
            if (isChrome()) {
              clearAuthStore();
              resetApp(ActiveScreen.LOGIN);
            } else {
              resetApp(ActiveScreen.CONNECT);
            }

            return Promise.reject(err);
          }

          // For Outlook, use logic to refresh the Microsoft token
          if (isOutlook()) {
            originalConfig._retry = true;

            try {
              const newToken = await getAccessToken();
              originalConfig.headers.Authorization = createBearerToken(newToken);
              return retryRequest(originalConfig);
            } catch (e) {
              resetApp(ActiveScreen.CONNECT);
              return Promise.reject(e);
            }
          }

          // For browser platforms, use queue-based retry logic with Meridian token refresh
          // if we're already refreshing, queue this request
          if (isRefreshing) {
            originalConfig._retry = true;
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject, config: originalConfig });
            });
          }

          originalConfig._retry = true;
          isRefreshing = true;

          try {
            // Use authToken and refreshToken from hook closure
            if (!authToken || !refreshToken) {
              clearAuthStore();
              resetApp(ActiveScreen.LOGIN);
              return Promise.reject(new Error("Missing auth token or refresh token"));
            }

            // Refresh the Meridian OAuth token
            const tokenData = await userService.refreshOAuthToken(authToken, refreshToken);

            if (tokenData?.access_token) {
              const newToken = tokenData.access_token;
              const newRefreshToken = tokenData.refresh_token;

              // Update auth store with new tokens (both are always returned)
              setTokens(newToken, newRefreshToken);

              setDefaultHeader("Authorization", createBearerToken(newToken));
              originalConfig.headers.Authorization = createBearerToken(newToken);
              processQueue(null, newToken);

              return retryRequest(originalConfig);
            } else {
              clearAuthStore();
              resetApp(ActiveScreen.LOGIN);
              return Promise.reject(new Error("Failed to refresh the OAuth token"));
            }
          } catch (e) {
            clearAuthStore();
            processQueue(e, null);
            resetApp(ActiveScreen.LOGIN);
            return Promise.reject(e);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(err);
      }
    );

    return () => {
      eject();
    };
  }, [authToken, refreshToken]);

  // For Chrome platform, show the connection screen if the user doesn't have a valid Google grant.
  useEffect(() => {
    if (isChrome() && user && !hasGoogleGrant) {
      changeActiveScreen(ActiveScreen.CONNECT);
    }
  }, [hasSession, user, hasGoogleGrant]);
}
